// The frame around every non-canvas screen: wordmark, breadcrumb, navigation,
// theme, and who you are.

import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "../api.js";
import { Avatar } from "../ui/Avatar.jsx";
import { Menu } from "../ui/Menu.jsx";
import { HexMark, Monitor, Moon, Sun } from "../ui/icons.jsx";
import { useTheme } from "../ui/theme.js";
import { useIsAdmin, useUser } from "./AuthGate.jsx";

const THEME_ICON = { system: Monitor, light: Sun, dark: Moon };
const THEME_LABEL = { system: "Match the system", light: "Light", dark: "Dark" };

const ThemeButton = () => {
  const { theme, cycle } = useTheme();
  const Icon = THEME_ICON[theme];
  return (
    <button
      type="button"
      className="btn ghost icon"
      onClick={cycle}
      title={`Theme: ${THEME_LABEL[theme]}`}
      aria-label={`Theme: ${THEME_LABEL[theme]}. Click to change.`}
    >
      <Icon />
    </button>
  );
};

export const Shell = ({ crumbs = [], actions, children }) => {
  const user = useUser();
  const isAdmin = useIsAdmin();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const signOut = async () => {
    await api.logout();
    window.location.href = "/";
  };

  return (
    <div className="shell">
      <header className="topbar">
        <div className="topbar-left">
          <Link to="/" className="brand">
            <HexMark className="mark" />
            canvas
          </Link>
          <nav className="crumbs">
            {crumbs.map((crumb) => (
              <span key={crumb.label} className="crumb row">
                <span className="sep">/</span>
                {crumb.to ? (
                  <Link to={crumb.to} className="truncate">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="truncate">{crumb.label}</span>
                )}
              </span>
            ))}
          </nav>
        </div>

        <div className="topbar-right">
          {isAdmin ? (
            <Link to="/admin" className={pathname === "/admin" ? "navlink current" : "navlink"}>
              Admin
            </Link>
          ) : null}
          {actions}
          <ThemeButton />
          <Menu
            label={`Signed in as ${user?.name ?? user?.email}`}
            className="btn ghost icon"
            items={[
              { label: user?.email ?? "Signed in" },
              "separator",
              ...(isAdmin ? [{ label: "Admin", onSelect: () => navigate("/admin") }] : []),
              { label: "Sign out", onSelect: () => void signOut() },
            ]}
          >
            <Avatar user={user} size={26} />
          </Menu>
        </div>
      </header>
      <main className="content">{children}</main>
    </div>
  );
};

export const PageHead = ({ title, lede, actions }) => (
  <div className="page-head">
    <div>
      <h1>{title}</h1>
      {lede ? <p className="lede">{lede}</p> : null}
    </div>
    {actions ? <div className="page-actions">{actions}</div> : null}
  </div>
);

export const EmptyState = ({ title, hint, action }) => (
  <div className="empty">
    <p className="empty-title">{title}</p>
    {hint ? <p className="muted">{hint}</p> : null}
    {action}
  </div>
);

export const ErrorNote = ({ error, onDismiss }) =>
  error ? (
    <div className="error-note" role="alert">
      <span>{error}</span>
      {onDismiss ? (
        <button className="btn ghost small" onClick={onDismiss}>
          Dismiss
        </button>
      ) : null}
    </div>
  ) : null;
